<template>
  <v-card>
    <v-card-title>
      <span class="info-text">{{ $t('reports_ui.fleet_health_report.vehicle_issues_trend') }}</span>
      <info-modal>
        <v-card-title>
          {{ $t('reports_ui.fleet_health_report.vehicle_issues_trend') }}
        </v-card-title>
        <v-card-text v-html="$t('reports_ui.fleet_health_report.modal_info.vehicle_issues_trend')" />
      </info-modal>
    </v-card-title>
    <v-card-text>
      <div v-if="overallAverageIssueAge" class="text-subtitle-1">
        {{ $t('reports_ui.fleet_health_report.overall_average_issue_age') }}: {{ $tc('unit_days', overallAverageIssueAge, { unit: overallAverageIssueAge }) }}
      </div>
      <div v-if="maxIssueAge" class="text-subtitle-1 mb-2">
        {{ $t('reports_ui.fleet_health_report.max_issue_age') }}: {{ $tc('unit_days', maxIssueAge, { unit: maxIssueAge }) }}
      </div>
      <c3-chart :height="350" :data="chartData" :config="chartConfig" type='line' />
      <div class="text-center text--accent-1">
        {{ $t('reports_ui.fleet_health_report.open_vehicle_issues_over_time')}}
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import InfoModal from '@/components/shared/InfoModal'
import { filter, map, isEmpty, keys, get, meanBy, max, round } from 'lodash'

export default {
  name: 'VehicleIssueTrends',
  components: {
    C3Chart,
    InfoModal
  },
  props: {
    customerReport: {
      type: Object
    }
  },
  computed: {
    /**
     * Get history for all dates that have it, filtering out those that don't.
     */
    filteredHistory () {
      return filter(this.customerReport.insights_dashboard_history, x => !isEmpty(x))
    },
    /**
     * The overall average vehicle issue age.
     */
    overallAverageIssueAge () {
      let val = round(meanBy(this.filteredHistory, x => parseFloat(x.average_issue_age_in_days)))
      if (isNaN(val)) {
        val = 0
      }
      return val.toLocaleString()
    },
    /**
     * The overall maximum vehicle issue age.
     */
    maxIssueAge () {
      const maxItem = max(this.filteredHistory, x => parseFloat(x.max_issue_age_in_days))
      let val = round(get(maxItem, 'max_issue_age_in_days'))
      if (isNaN(val)) {
        val = 0
      }
      return val.toLocaleString()
    },
    /**
     * Format the chart data needed for the graph.
     * @returns Object
     */
    chartData () {
      const dateColumn = keys(this.customerReport.insights_dashboard_history)
      dateColumn.unshift('date')

      const swappedColumn = this.getMappedData('sensors_swapped')
      swappedColumn.unshift(this.$t('sensors_swapped'))

      const coolerSensorFailureColumn = this.getMappedData('cooler_sensor_failure')
      coolerSensorFailureColumn.unshift(this.$t('fleet_health_ui.cooler_sensor_failure'))

      const freezerSensorFailureColumn = this.getMappedData('freezer_sensor_failure')
      freezerSensorFailureColumn.unshift(this.$t('fleet_health_ui.freezer_sensor_failure'))

      const coolerTemperatureHealthColumn = this.getMappedData('cooler_temperature_health')
      coolerTemperatureHealthColumn.unshift(this.$t('cooler_temperature_health_warning'))

      const freezerTemperatureHealthColumn = this.getMappedData('freezer_temperature_health')
      freezerTemperatureHealthColumn.unshift(this.$t('freezer_temperature_health_warning'))

      return {
        x: 'date',
        columns: [
          dateColumn,
          swappedColumn,
          coolerSensorFailureColumn,
          freezerSensorFailureColumn,
          coolerTemperatureHealthColumn,
          freezerTemperatureHealthColumn
        ]
      }
    },
    /**
     * The chart config.
     */
    chartConfig () {
      return {
        axis: {
          x: {
            label: {
              text: 'Date'
            },
            type: 'timeseries',
            tick: {
              format: '%Y-%m-%d',
              centered: true
            }
          },
          y: {
            label: {
              text: this.$t('number_of_vehicles')
            },
            tick: {
              format: function (d) {
                return parseInt(d) === d ? d : null
              },
              culling: true
            }
          }
        },
        line: {
          connectNull: true
        }
      }
    }
  },
  methods: {
    /**
     * Convenience method to get data from the insights_dashboard_history field.
     * @param field
     * @returns {*}
     */
    getMappedData (field) {
      return map(this.customerReport.insights_dashboard_history, x => get(x, field, null))
    }
  }
}
</script>

<style scoped>

</style>
